<script setup lang="ts">
import { onMounted, ref, computed } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useNotificationStore } from '@/stores/notification'
import { useConfirm } from '@/stores/confirm'
import { authService } from '@/api/auth'
import type { AxiosError } from 'axios'
import WelcomeBanner from '@/components/farm/supervisor/WelcomeBanner.vue'

interface User {
  id: string
  username: string
  email: string
  first_name: string
  last_name: string
  role: 'ADMIN' | 'SUPERVISOR' | 'CUSTOMER' | 'EMPLOYEE'
  is_active: boolean
  date_joined: string
  last_login: string | null
  created_by: string | null
  owner_id: string | null
  created_by_name: string
  owner_name: string
}

const authStore = useAuthStore()
const notificationStore = useNotificationStore()
const { confirm } = useConfirm()
const router = useRouter()
const users = ref<User[]>([]) // Ensure initialized as empty array
const loading = ref(false)
const error = ref<string | null>(null)
const showModal = ref(false)
const isEditing = ref(false)
const selectedUser = ref<User | null>(null)
const form = ref({
  username: '',
  email: '',
  first_name: '',
  last_name: '',
  password: '',
  confirmPassword: '',
  role: 'SUPERVISOR' as 'ADMIN' | 'SUPERVISOR' | 'CUSTOMER' | 'EMPLOYEE',
  is_active: true,
})
const errors = ref<Record<string, string>>({})
const filterRole = ref<string>('All')
const searchQuery = ref<string>('')
const currentPage = ref(1)
const pageSize = 10

const filteredUsers = computed(() => {
  const currentUser = authStore.user
  if (!currentUser) return []

  let filtered = users.value.filter((user) => {
    if (currentUser.role === 'ADMIN') {
      return user.owner_id === currentUser.id || !user.owner_id
    } else if (currentUser.role === 'SUPERVISOR') {
      return user.owner_id === currentUser.owner_id || !user.owner_id
    }
    return false
  })

  if (filterRole.value !== 'All') {
    filtered = filtered.filter((user) => user.role === filterRole.value)
  }

  if (searchQuery.value.trim()) {
    const query = searchQuery.value.toLowerCase().trim()
    filtered = filtered.filter(
      (user) =>
        user.first_name.toLowerCase().includes(query) ||
        user.last_name.toLowerCase().includes(query)
    )
  }

  const start = (currentPage.value - 1) * pageSize
  const end = start + pageSize
  return filtered.slice(start, end).map((user, index) => ({
    ...user,
    rowNumber: start + index + 1,
  }))
})



const totalPages = computed(() =>
  Math.ceil(
    users.value
      .filter((user) => {
        if (authStore.user?.role === 'ADMIN') {
          return user.owner_id === authStore.user.id || !user.owner_id
        } else if (authStore.user?.role === 'SUPERVISOR') {
          return user.owner_id === authStore.user.owner_id || !user.owner_id
        }
        return false
      })
      .filter(
        (user) =>
          filterRole.value === 'All' || user.role === filterRole.value,
      )
      .filter(
        (user) =>
          !searchQuery.value.trim() ||
          user.first_name.toLowerCase().includes(searchQuery.value.toLowerCase().trim()) ||
          user.last_name.toLowerCase().includes(searchQuery.value.toLowerCase().trim()),
      ).length / pageSize,
  ),
)


onMounted(async () => {
  if (
    !authStore.isAuthenticated ||
    !(authStore.user?.role === 'ADMIN' || authStore.user?.role === 'SUPERVISOR')
  ) {
    router.push('/login')
    return
  }
  await fetchUsers()
})

const fetchUsers = async () => {
  loading.value = true
  error.value = null
  try {
    const role = filterRole.value !== 'All' ? filterRole.value : undefined
    const response = await authService.getUsers(role)
    // Ensure response is an array of User objects
    users.value = Array.isArray(response) ? response : []
  } catch (err: unknown) {
    const axiosError = err as AxiosError
    error.value = 'Failed to load users'
    console.error('Fetch users error:', axiosError.response?.data || axiosError)
    users.value = [] // Reset to empty array on error
  } finally {
    loading.value = false
  }
}

const openCreateModal = () => {
  showModal.value = true
  isEditing.value = false
  errors.value = {}
  form.value = {
    username: '',
    email: '',
    first_name: '',
    last_name: '',
    password: '',
    confirmPassword: '',
    role: 'SUPERVISOR',
    is_active: true,
  }
}

const openEditModal = (user: User) => {
  showModal.value = true
  isEditing.value = true
  selectedUser.value = user
  errors.value = {}
  form.value = {
    username: user.username,
    email: user.email,
    first_name: user.first_name,
    last_name: user.last_name,
    password: '',
    confirmPassword: '',
    role: user.role,
    is_active: user.is_active,
  }
}

const closeModal = () => {
  showModal.value = false
  selectedUser.value = null
}

const submitForm = async () => {
  loading.value = true
  errors.value = {}

  try {
    if (isEditing.value && selectedUser.value) {
      const updateData = {
        username: form.value.username,
        email: form.value.email,
        first_name: form.value.first_name,
        last_name: form.value.last_name,
        role: form.value.role,
        is_active: form.value.is_active,
        ...(form.value.password ? { password: form.value.password } : {}),
        ...(form.value.password ? { confirmPassword: form.value.confirmPassword } : {}),
      }
      await authService.updateUser(selectedUser.value.id, updateData)
      notificationStore.showSuccess('Staff member updated successfully!')
    } else {
      await authService.createUser(form.value)
      notificationStore.showSuccess('Staff member created successfully!')
    }
    await fetchUsers()
    showModal.value = false
  } catch (err: unknown) {
    const axiosError = err as AxiosError<{ errors?: Record<string, string> }>
    console.error('Submit error:', axiosError.response?.data || axiosError)
    if (axiosError.response?.data?.errors) {
      errors.value = axiosError.response.data.errors
    } else {
      errors.value = {
        general: `An error occurred while ${isEditing.value ? 'updating' : 'creating'} the user`,
      }
    }
    notificationStore.showError(errors.value.general || 'Failed to save staff member.')
  } finally {
    loading.value = false
  }
}

const toggleUserActive = async (user: User) => {
  const action = user.is_active ? 'deactivate' : 'activate'
  const confirmed = await confirm({
    title: `${user.is_active ? 'Deactivate' : 'Activate'} User`,
    message: `Are you sure you want to ${action} ${user.username}?`,
    confirmText: user.is_active ? 'Deactivate' : 'Activate',
    type: user.is_active ? 'warning' : 'info'
  })
  if (!confirmed) return
  loading.value = true
  try {
    await authService.toggleUserActive(user.id, !user.is_active)
    notificationStore.showSuccess(`User successfully ${user.is_active ? 'deactivated' : 'activated'}.`)
    await fetchUsers()
  } catch (err: unknown) {
    const axiosError = err as AxiosError
    error.value = `Failed to ${action} user`
    notificationStore.showError(`Failed to ${action} user`)
    console.error('Toggle active error:', axiosError.response?.data || axiosError)
  } finally {
    loading.value = false
  }
}

const deleteUser = async (id: string) => {
  const user = users.value.find(u => u.id === id)
  const confirmed = await confirm({
    title: 'Delete Staff Member',
    message: `Are you sure you want to delete staff member "${user ? user.first_name + ' ' + user.last_name : 'selected'}"? This action cannot be undone.`,
    confirmText: 'Delete',
    type: 'danger'
  })
  if (!confirmed) return

  loading.value = true
  try {
    await authService.deleteUser(id)
    notificationStore.showSuccess('Staff member deleted successfully.')
    await fetchUsers()
  } catch (err: unknown) {
    const axiosError = err as AxiosError
    error.value = 'Failed to delete user'
    notificationStore.showError(error.value)
    console.error('Delete user error:', axiosError.response?.data || axiosError)
  } finally {
    loading.value = false
  }
}

const changePage = (page: number) => {
  if (page >= 1 && page <= totalPages.value) {
    currentPage.value = page
  }
}

const filterByRole = () => {
  currentPage.value = 1
  fetchUsers()
}
</script>

<template>
  <div class="px-4 py-6 sm:px-6 lg:px-8 lg:pl-64">
    <WelcomeBanner/>
    <div class="sm:flex sm:items-center">
      <div class="sm:flex-auto">
        <h1 class="text-xl font-semibold text-gray-900">Users</h1>
        <p class="mt-2 text-sm text-gray-700">List of all users in the system</p>
      </div>
      <div class="mt-4 sm:mt-0 sm:ml-16 sm:flex-none">
        <button
          type="button"
          class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors duration-200"
          @click="openCreateModal"
        >
          Create User
        </button>
      </div>
    </div>

    <div class="mt-4 flex flex-col sm:flex-row sm:items-center space-y-4 sm:space-y-0 sm:space-x-4">
      <div>
        <label for="search" class="block text-sm font-medium text-gray-700">Search by Name</label>
        <input
          v-model="searchQuery"
          type="text"
          id="search"
          placeholder="Search by first or last name"
          class="mt-1 block w-full sm:w-48 border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm transition-all duration-200"
        />
      </div>
      <div>
        <label for="roleFilter" class="block text-sm font-medium text-gray-700"
          >Filter by Role</label
        >
        <select
          v-model="filterRole"
          id="roleFilter"
          class="mt-1 block w-full sm:w-48 border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm transition-all duration-200"
          @change="filterByRole"
        >
          <option value="All">All</option>
          <option value="SUPERVISOR">Supervisor</option>
          <option value="CUSTOMER">Customer</option>
          <option value="EMPLOYEE">Employee</option>
        </select>
      </div>
    </div>

    <div class="mt-8 bg-white shadow rounded-lg p-6">
      <div v-if="loading" class="text-center">
        <p class="text-gray-500 animate-pulse">Loading users...</p>
      </div>
      <div v-else-if="error" class="text-center text-red-500">
        {{ error }}
      </div>
      <div v-else-if="filteredUsers.length === 0" class="text-center">
        <p class="text-gray-500 text-sm">No users found</p>
      </div>
      <div v-else>
        <div class="overflow-x-auto">
          <table class="min-w-full divide-y divide-gray-200">
            <thead class="bg-gray-50">
              <tr>
                <th
                  class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  No.
                </th>
                <th
                  class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  Name
                </th>
                <th
                  class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  Email
                </th>
                <th
                  class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  Role
                </th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created By</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Owner</th>
                <th
                  class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  Actions
                </th>
              </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-200">
              <tr
                v-for="user in filteredUsers"
                :key="user.id"
                :class="[
                  user.is_active ? 'bg-white' : 'bg-gray-300',
                  'hover:bg-gray-50 transition-transform duration-200 transform hover:scale-[1.0001]',
                ]"
              >
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {{ user.rowNumber }}
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  <div class="flex items-center">
                    <div
                      class="flex-shrink-0 h-8 w-8 rounded-full bg-indigo-600 text-white flex items-center justify-center font-medium"
                    >
                      {{ user.first_name.charAt(0) }}{{ user.last_name.charAt(0) }}
                    </div>
                    <div class="ml-3">{{ user.first_name }} {{ user.last_name }}</div>
                  </div>
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{{ user.email }}</td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{{ user.role }}</td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {{ user.created_by_name }}
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {{ user.owner_name }}
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <button
                    class="text-indigo-600 hover:text-indigo-900 mr-4 transition-colors duration-200"
                    @click="openEditModal(user)"
                  >
                    Edit
                  </button>
                  <!-- <button
                    :class="[
                      user.is_active
                        ? 'text-yellow-600 hover:text-yellow-900'
                        : 'text-green-600 hover:text-green-900',
                      'mr-4 transition-colors duration-200',
                    ]"
                    @click="toggleUserActive(user)"
                  >
                    {{ user.is_active ? 'Deactivate' : 'Activate' }}
                  </button> -->
                  <button
                    class="text-red-600 hover:text-red-900 transition-colors duration-200"
                    @click="deleteUser(user.id)"
                  >
                    Delete
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div class="mt-4 flex justify-between items-center">
          <div class="text-sm text-gray-700">
            Showing {{ (currentPage - 1) * pageSize + 1 }} to
            {{ Math.min(currentPage * pageSize, users.length) }} of {{ users.length }} users
          </div>
          <div class="flex space-x-2">
            <button
              :disabled="currentPage === 1"
              class="px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 transition-colors duration-200"
              @click="changePage(currentPage - 1)"
            >
              Previous
            </button>
            <button
              v-for="page in totalPages"
              :key="page"
              :class="[
                'px-3 py-1 border rounded-md text-sm font-medium',
                currentPage === page
                  ? 'bg-indigo-600 text-white border-indigo-600'
                  : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50',
                'transition-colors duration-200',
              ]"
              @click="changePage(page)"
            >
              {{ page }}
            </button>
            <button
              :disabled="currentPage === totalPages"
              class="px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 transition-colors duration-200"
              @click="changePage(currentPage + 1)"
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal -->
    <div
      v-if="showModal"
      class="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center transition-opacity duration-300"
      :class="{ 'opacity-100': showModal, 'opacity-0': !showModal }"
    >
      <div
        class="relative bg-white rounded-lg shadow-xl max-w-md w-full mx-4 sm:mx-0 transform transition-transform duration-300 scale-100"
      >
        <div class="px-6 py-4 border-b border-gray-200">
          <h3 class="text-lg font-medium text-gray-900">
            {{ isEditing ? 'Edit User' : 'Create New User' }}
          </h3>
          <button
            class="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors duration-200"
            @click="closeModal"
          >
            <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>
        <form @submit.prevent="submitForm" class="px-6 py-4">
          <div v-if="errors.general" class="mb-4 text-red-600 text-sm">{{ errors.general }}</div>
          <div class="grid grid-cols-1 gap-4">
            <div>
              <label for="username" class="block text-sm font-medium text-gray-700">Username</label>
              <input
                v-model="form.username"
                type="text"
                id="username"
                class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm transition-all duration-200"
                :class="{ 'border-red-500': errors.username }"
              />
              <p v-if="errors.username" class="mt-1 text-red-600 text-sm">{{ errors.username }}</p>
            </div>
            <div>
              <label for="email" class="block text-sm font-medium text-gray-700">Email</label>
              <input
                v-model="form.email"
                type="email"
                id="email"
                class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm transition-all duration-200"
                :class="{ 'border-red-500': errors.email }"
              />
              <p v-if="errors.email" class="mt-1 text-red-600 text-sm">{{ errors.email }}</p>
            </div>
            <div>
              <label for="first_name" class="block text-sm font-medium text-gray-700"
                >First Name</label
              >
              <input
                v-model="form.first_name"
                type="text"
                id="first_name"
                class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm transition-all duration-200"
                :class="{ 'border-red-500': errors.first_name }"
              />
              <p v-if="errors.first_name" class="mt-1 text-red-600 text-sm">
                {{ errors.first_name }}
              </p>
            </div>
            <div>
              <label for="last_name" class="block text-sm font-medium text-gray-700"
                >Last Name</label
              >
              <input
                v-model="form.last_name"
                type="text"
                id="last_name"
                class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm transition-all duration-200"
                :class="{ 'border-red-500': errors.last_name }"
              />
              <p v-if="errors.last_name" class="mt-1 text-red-600 text-sm">
                {{ errors.last_name }}
              </p>
            </div>
            <div>
              <label for="role" class="block text-sm font-medium text-gray-700">Role</label>
              <select
                v-model="form.role"
                id="role"
                class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm transition-all duration-200"
                :class="{ 'border-red-500': errors.role }"
              >
                <option value="SUPERVISOR">Supervisor</option>
                <option value="CUSTOMER">Customer</option>
                <option value="EMPLOYEE">Employee</option>
              </select>
              <p v-if="errors.role" class="mt-1 text-red-600 text-sm">{{ errors.role }}</p>
            </div>
            <div>
              <label for="is_active" class="block text-sm font-medium text-gray-700">Status</label>
              <select
                v-model="form.is_active"
                id="is_active"
                class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm transition-all duration-200"
                :class="{ 'border-red-500': errors.is_active }"
              >
                <option :value="true">Active</option>
                <option :value="false">Inactive</option>
              </select>
              <p v-if="errors.is_active" class="mt-1 text-red-600 text-sm">
                {{ errors.is_active }}
              </p>
            </div>
            <div v-if="!isEditing">
              <label for="password" class="block text-sm font-medium text-gray-700">Password</label>
              <input
                v-model="form.password"
                type="password"
                id="password"
                class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm transition-all duration-200"
                :class="{ 'border-red-500': errors.password }"
              />
              <p v-if="errors.password" class="mt-1 text-red-600 text-sm">{{ errors.password }}</p>
            </div>
            <div v-if="!isEditing">
              <label for="confirmPassword" class="block text-sm font-medium text-gray-700"
                >Confirm Password</label
              >
              <input
                v-model="form.confirmPassword"
                type="password"
                id="confirmPassword"
                class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm transition-all duration-200"
                :class="{ 'border-red-500': errors.confirmPassword }"
              />
              <p v-if="errors.confirmPassword" class="mt-1 text-red-600 text-sm">
                {{ errors.confirmPassword }}
              </p>
            </div>
          </div>
          <div class="mt-6 flex justify-end space-x-3">
            <button
              type="button"
              class="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors duration-200"
              @click="closeModal"
            >
              Cancel
            </button>
            <button
              type="submit"
              class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors duration-200"
              :disabled="loading"
            >
              <span v-if="loading">{{ isEditing ? 'Updating...' : 'Creating...' }}</span>
              <span v-else>{{ isEditing ? 'Update User' : 'Create User' }}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Ensure modal animations work smoothly */
.modal-enter-active,
.modal-leave-active {
  transition:
    opacity 0.3s ease,
    transform 0.3s ease;
}
.modal-enter-from,
.modal-leave-to {
  opacity: 0;
  transform: scale(0.95);
}
</style>
